/**
 * Phase 3 — HR Bench Reports.
 *
 * Filtered CSV exports of `hr_bench_entries` across all 13 report types
 * required by the spec. Backend `routes_hr_bench.download_report` is the
 * single source — no duplicate logic per report.
 */
import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileBarChart, Download, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function HRBenchReports() {
  const [activeCompany] = useActiveCompany();
  const [reports, setReports] = useState([]);
  const [period, setPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    enabled: false,
  });
  const [busy, setBusy] = useState({});

  useEffect(() => {
    axios.get(`${API}/hr-bench/reports`, { withCredentials: true })
      .then(r => setReports(r.data?.reports || []))
      .catch(() => toast.error('Failed to load reports'));
  }, []);

  const download = async (kind) => {
    setBusy(b => ({ ...b, [kind]: true }));
    try {
      const params = new URLSearchParams();
      if (activeCompany) params.set('company_id', activeCompany);
      if (period.enabled) {
        params.set('period_year', period.year);
        params.set('period_month', period.month);
      }
      const r = await axios.get(`${API}/hr-bench/reports/${kind}.csv?${params.toString()}`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `hr_bench_${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${kind} report`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(b => ({ ...b, [kind]: false }));
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="hr-bench-reports-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <FileBarChart className="w-7 h-7 text-violet-600" />
              HR Bench Reports
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              All reports below are filtered CSV exports of the controlled <code className="text-xs bg-slate-100 px-1 rounded">hr_bench_entries</code> collection — single source, no duplicate logic.
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Filter className="w-4 h-4 text-slate-500" />
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox"
                data-testid="hr-bench-reports-period-toggle"
                checked={period.enabled}
                onChange={e => setPeriod(p => ({ ...p, enabled: e.target.checked }))} />
              <span>Filter by period</span>
            </label>
            {period.enabled && (
              <>
                <select data-testid="hr-bench-reports-month" value={period.month}
                  onChange={e => setPeriod(p => ({ ...p, month: Number(e.target.value) }))}
                  className="px-2 py-1 border rounded">
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <input type="number"
                  data-testid="hr-bench-reports-year"
                  value={period.year}
                  onChange={e => setPeriod(p => ({ ...p, year: Number(e.target.value) }))}
                  className="px-2 py-1 border rounded w-20" />
              </>
            )}
            <span className="ml-auto text-slate-500">Company filter is taken from header selector</span>
          </div>
        </Card>

        {/* Reports grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {reports.map(r => (
            <Card key={r.key} className="p-4 hover:shadow-md transition" data-testid={`report-card-${r.key}`}>
              <div className="text-sm font-semibold text-slate-800 mb-1">{r.label}</div>
              <div className="text-[10px] uppercase tracking-wider text-violet-700 mb-3">{r.scope}</div>
              <Button
                size="sm"
                onClick={() => download(r.key)}
                disabled={busy[r.key]}
                className="bg-violet-600 hover:bg-violet-700 text-white text-xs"
                data-testid={`download-report-${r.key}`}
              >
                {busy[r.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                Download CSV
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </MainLayout>
  );
}
